import { useEffect, useRef } from 'react';
import { Box } from '@mui/material';

// Animação de fundo estilo "constelação" pro painel escuro do login —
// mistura constelações reais (7 no total: Órion, Escorpião, Cruzeiro do Sul,
// Ursa Maior, Touro, Cão Maior, Cassiopeia) com pontos
// soltos ao redor, todos devagar e discretos. 3 "slots" de constelação ficam
// visíveis por vez; cada slot tem seu próprio ciclo independente de
// aparecer/sumir (ver atualizarSlot) — depois de um tempo visível, some em
// fade, troca (enquanto invisível) pra outra constelação do pool que não
// esteja em uso nos outros slots, e volta a aparecer em fade noutro lugar.
// Isso faz o conjunto ir variando aos poucos enquanto a tela fica aberta,
// em vez de ficar travado nas mesmas 3 a visita inteira. Qual trio começa
// visível ainda depende da hora em que a tela é aberta (ver
// escolherConstelacoesDoHorario), só o ponto de partida muda por hora — a
// rotação depois disso é a mesma sempre. 100% Canvas (sem lib de
// partículas), na paleta ciano já usada no resto do login (ver SIAC.cyan em
// Login.tsx) em vez de uma cor genérica.
//
// É todo o fundo do painel escuro do login agora (substitui a decoração
// estática que existia antes, LoginTechDecoration.tsx) — fica atrás do
// conteúdo real do painel (zIndex 1 aqui, conteúdo em zIndex 2 no Login.tsx),
// sobre o gradiente de fundo já definido ali.

interface IParticula {
  // Posição exibida (a que é desenhada e usada nas conexões) — só ela pode
  // se afastar da formação, quando essa é a partícula sendo puxada pro mouse.
  x: number;
  y: number;
  // Posição "natural": é a formação de verdade — pontos soltos avançam por
  // vx/vy; estrelas de constelação seguem a âncora do próprio slot (ver
  // atualizarNaturais()). Quando a partícula não está selecionada, x/y é
  // reconduzida suavemente até aqui.
  naturalX: number;
  naturalY: number;
  vx: number;
  vy: number;
  raio: number;
  opacidadeBase: number;
  // Presente só nas estrelas de constelação — de qual slot essa estrela faz
  // parte (pra buscar a opacidade de fade do slot no momento de desenhar), o
  // deslocamento fixo em relação à âncora do slot (a "forma" da constelação
  // nunca muda, só a âncora desliza devagar pelo painel), e os nomes pro
  // rótulo discreto ao passar o mouse (ver passo()). nomeEstrela fica de
  // fora nos pontos que são só "conectores" do desenho, sem uma estrela
  // nomeada de verdade ali — o rótulo mostra só a constelação nesse caso.
  grupo?: {
    slotIndex: number;
    offsetX: number;
    offsetY: number;
    nomeConstelacao: string;
    nomeEstrela?: string;
    // Só presente quando nomeEstrela também está — usado no rótulo de
    // clique (ver handleClick), não no de hover.
    distanciaAnosLuz?: number;
  };
}

interface IAncoraGrupo {
  x: number;
  y: number;
  vx: number;
  vy: number;
  // Metade da maior dimensão da constelação — usado como margem de
  // wraparound, pra âncora só trocar de lado quando o grupo INTEIRO já saiu
  // da tela, não a partir do centro dele.
  raioGrupo: number;
}

// Coordenadas relativas de cada constelação (não astronomicamente exatas —
// proporção visual simplificada da forma clássica de "ligar os pontos"),
// centradas em (0,0) numa escala própria de pixels que depois é multiplicada
// por `escala` (ver criarAncoraParaTemplate/criarParticulasSlot). `arestas`
// são pares de índices (dentro da própria lista de estrelas) formando o
// desenho tradicional.
interface IEstrelaTemplate {
  x: number;
  y: number;
  // 0 a 1 — estrelas principais (as que dão nome/forma reconhecível) ficam
  // mais brilhantes e um pouco maiores que o resto.
  brilho: number;
  // Nome de verdade da estrela (astronômico) — quando ausente, é só um
  // ponto de conexão do desenho, sem nome pra mostrar no rótulo ao passar
  // o mouse.
  nome?: string;
  // Distância real aproximada da Terra, em anos-luz — só nas estrelas
  // nomeadas (valores de referência astronômica, arredondados). Mostrada ao
  // CLICAR na estrela (ver handleClick), separado do rótulo de hover.
  distanciaAnosLuz?: number;
}
interface IConstelacaoTemplate {
  nome: string;
  estrelas: IEstrelaTemplate[];
  arestas: [number, number][];
}

// Órion: ombros (Betelgeuse/Bellatrix) → cinturão (Mintaka/Alnilam/Alnitak —
// as "Três Marias", brilho máximo) → pés (Saiph/Rigel). Ampulheta clássica.
const ORION: IConstelacaoTemplate = {
  nome: 'Órion',
  estrelas: [
    { x: -42, y: -72, brilho: 0.85, nome: 'Betelgeuse', distanciaAnosLuz: 640 },
    { x: 40, y: -78, brilho: 0.7, nome: 'Bellatrix', distanciaAnosLuz: 250 },
    { x: -20, y: -8, brilho: 1, nome: 'Mintaka (Três Marias)', distanciaAnosLuz: 900 },
    { x: 0, y: -3, brilho: 1, nome: 'Alnilam (Três Marias)', distanciaAnosLuz: 2000 },
    { x: 19, y: 2, brilho: 1, nome: 'Alnitak (Três Marias)', distanciaAnosLuz: 800 },
    { x: -36, y: 74, brilho: 0.8, nome: 'Saiph', distanciaAnosLuz: 650 },
    { x: 46, y: 80, brilho: 0.9, nome: 'Rigel', distanciaAnosLuz: 860 },
  ],
  arestas: [
    [0, 2], [1, 4],
    [2, 3], [3, 4],
    [2, 5], [4, 6],
    [0, 1],
  ],
};

// Escorpião: gancho longo característico, do "ferrão da cabeça" até a
// cauda curvada — Antares (coração, vermelha) como estrela mais brilhante.
const ESCORPIAO: IConstelacaoTemplate = {
  nome: 'Escorpião',
  estrelas: [
    { x: -58, y: -46, brilho: 0.55, nome: 'Acrab', distanciaAnosLuz: 400 },
    { x: -38, y: -54, brilho: 0.6, nome: 'Dschubba', distanciaAnosLuz: 400 },
    { x: -18, y: -38, brilho: 1, nome: 'Antares', distanciaAnosLuz: 550 },
    { x: 2, y: -14, brilho: 0.65, nome: 'Alniyat', distanciaAnosLuz: 500 },
    { x: 16, y: 12, brilho: 0.6 }, // conector, sem estrela nomeada
    { x: 26, y: 40, brilho: 0.65, nome: 'Sargas', distanciaAnosLuz: 270 },
    { x: 16, y: 64, brilho: 0.7, nome: 'Lesath', distanciaAnosLuz: 500 },
    { x: -8, y: 74, brilho: 0.85, nome: 'Shaula', distanciaAnosLuz: 570 }, // ferrão
  ],
  arestas: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 7]],
};

// Cruzeiro do Sul — pipa/cruz de 4 estrelas principais + 1 secundária,
// bem mais compacta e reconhecível de longe que as outras duas.
const CRUZEIRO_DO_SUL: IConstelacaoTemplate = {
  nome: 'Cruzeiro do Sul',
  estrelas: [
    { x: 4, y: -48, brilho: 1, nome: 'Acrux', distanciaAnosLuz: 320 },
    { x: -6, y: 42, brilho: 0.9, nome: 'Gacrux', distanciaAnosLuz: 88 },
    { x: -34, y: -8, brilho: 0.75, nome: 'Mimosa', distanciaAnosLuz: 280 },
    { x: 30, y: 6, brilho: 0.65, nome: 'Imai', distanciaAnosLuz: 345 },
    { x: -10, y: -14, brilho: 0.4, nome: 'Ginan', distanciaAnosLuz: 230 },
  ],
  arestas: [[0, 1], [2, 3], [0, 4]],
};

// Ursa Maior — a "panela"/caçarola clássica (7 estrelas, tigela + cabo).
const URSA_MAIOR: IConstelacaoTemplate = {
  nome: 'Ursa Maior',
  estrelas: [
    { x: -60, y: -40, brilho: 0.75, nome: 'Dubhe', distanciaAnosLuz: 123 },
    { x: -60, y: 10, brilho: 0.65, nome: 'Merak', distanciaAnosLuz: 79 },
    { x: -20, y: 30, brilho: 0.6, nome: 'Phecda', distanciaAnosLuz: 83 },
    { x: 0, y: 10, brilho: 0.55, nome: 'Megrez', distanciaAnosLuz: 58 },
    { x: 30, y: 0, brilho: 0.7, nome: 'Alioth', distanciaAnosLuz: 81 },
    { x: 55, y: -15, brilho: 0.75, nome: 'Mizar', distanciaAnosLuz: 83 },
    { x: 75, y: -35, brilho: 0.8, nome: 'Alkaid', distanciaAnosLuz: 101 },
  ],
  arestas: [[0, 1], [1, 2], [2, 3], [3, 0], [3, 4], [4, 5], [5, 6]],
};

// Touro — o "V" do rosto do touro (aglomerado das Híades) com Aldebaran (o
// olho, avermelhada) e as duas pontas dos chifres.
const TOURO: IConstelacaoTemplate = {
  nome: 'Touro',
  estrelas: [
    { x: -45, y: -60, brilho: 0.65, nome: 'Elnath', distanciaAnosLuz: 134 },
    { x: -22, y: 8, brilho: 0.45 }, // conector do braço esquerdo do V
    { x: 0, y: -8, brilho: 1, nome: 'Aldebaran', distanciaAnosLuz: 65 },
    { x: 22, y: 12, brilho: 0.45 }, // conector do braço direito do V
    { x: 50, y: -55, brilho: 0.55, nome: 'Tianguan', distanciaAnosLuz: 440 },
  ],
  arestas: [[0, 1], [1, 2], [2, 3], [3, 4]],
};

// Cão Maior — dominado por Sirius, a estrela mais brilhante do céu noturno.
const CAO_MAIOR: IConstelacaoTemplate = {
  nome: 'Cão Maior',
  estrelas: [
    { x: 0, y: -45, brilho: 1, nome: 'Sirius', distanciaAnosLuz: 8.6 },
    { x: -22, y: -18, brilho: 0.5, nome: 'Mirzam', distanciaAnosLuz: 500 },
    { x: 15, y: 15, brilho: 0.6, nome: 'Wezen', distanciaAnosLuz: 1800 },
    { x: -10, y: 30, brilho: 0.55, nome: 'Adhara', distanciaAnosLuz: 430 },
    { x: 22, y: 45, brilho: 0.5, nome: 'Aludra', distanciaAnosLuz: 2000 },
  ],
  arestas: [[0, 1], [0, 2], [2, 3], [2, 4]],
};

// Cassiopeia — o "W" (ou "M", conforme a posição) bem característico, fácil
// de reconhecer mesmo pequeno.
const CASSIOPEIA: IConstelacaoTemplate = {
  nome: 'Cassiopeia',
  estrelas: [
    { x: -55, y: 15, brilho: 0.6, nome: 'Segin', distanciaAnosLuz: 440 },
    { x: -28, y: -20, brilho: 0.65, nome: 'Ruchbah', distanciaAnosLuz: 99 },
    { x: 0, y: 15, brilho: 0.7, nome: 'Navi', distanciaAnosLuz: 550 },
    { x: 28, y: -25, brilho: 0.75, nome: 'Schedar', distanciaAnosLuz: 228 },
    { x: 55, y: 5, brilho: 0.65, nome: 'Caph', distanciaAnosLuz: 55 },
  ],
  arestas: [[0, 1], [1, 2], [2, 3], [3, 4]],
};

// Pool completo — usado pelas trocas em runtime (ver escolherProximaConstelacao).
const POOL_CONSTELACOES: IConstelacaoTemplate[] = [
  ORION, ESCORPIAO, CRUZEIRO_DO_SUL, URSA_MAIOR, TOURO, CAO_MAIOR, CASSIOPEIA,
];

// Decorativo, não astronômico de verdade (visibilidade real depende de
// estação/hemisfério, não só da hora) — só pra dar variedade: quem abre o
// login de manhã, à tarde, à noite ou de madrugada começa vendo um trio
// diferente. Depois da abertura, a rotação (ver atualizarSlot) segue
// sorteando do pool inteiro, sem depender mais da hora.
const escolherConstelacoesDoHorario = (hora: number): IConstelacaoTemplate[] => {
  if (hora >= 0 && hora < 6) return [ORION, TOURO, CAO_MAIOR]; // madrugada
  if (hora >= 6 && hora < 12) return [URSA_MAIOR, CASSIOPEIA, CRUZEIRO_DO_SUL]; // manhã
  if (hora >= 12 && hora < 18) return [ESCORPIAO, CRUZEIRO_DO_SUL, CASSIOPEIA]; // tarde
  return [ORION, ESCORPIAO, CRUZEIRO_DO_SUL]; // noite — trio clássico original
};

// Escolhe a próxima constelação de um slot que ficou invisível — nunca uma
// que já esteja em uso (visível ou ainda em fade) noutro slot, pra nunca
// repetir duas iguais ao mesmo tempo na tela.
const escolherProximaConstelacao = (emUso: IConstelacaoTemplate[]): IConstelacaoTemplate => {
  const candidatas = POOL_CONSTELACOES.filter((c) => !emUso.includes(c));
  const lista = candidatas.length > 0 ? candidatas : POOL_CONSTELACOES;
  return lista[Math.floor(Math.random() * lista.length)];
};

// Galáxia — um brilho suave (núcleo + 2 "braços" achatados/rotacionados,
// tudo com gradiente radial, sem borda dura) que aparece em alguns
// momentos, fica um tempo, some de novo e reaparece depois noutro lugar —
// ver atualizarGalaxia(). Puramente decorativo, atrás de tudo o resto.
interface IGalaxia {
  x: number;
  y: number;
  raio: number;
  rotacao: number;
  velocidadeRotacao: number;
  opacidade: number; // 0 a 1, sempre suavizada (nunca pula direto pro alvo)
  ativa: boolean; // alvo atual da opacidade (true=1, false=0)
  framesRestantes: number; // até trocar de fase (ativa <-> escondida)
}

const COR_PONTO = '0, 226, 244'; // SIAC.cyan em rgb, pra montar rgba(...) com opacidades variadas
const DISTANCIA_MAX_LINHA = 150;
// Raio de "captura": só a partícula mais próxima do mouse dentro desse raio
// vira a selecionada (ver escolherSelecionada em passo()) — nenhuma outra é
// afetada.
const DISTANCIA_MOUSE = 160;
// Facilidade (fração da distância percorrida por frame) de quem está sendo
// puxada pro cursor — mais alta que o retorno, pra responder rápido ao mouse.
const FACILIDADE_SEGUIR = 0.08;
// Facilidade de quem foi solta (ou nunca foi selecionada) voltando/ficando
// na posição natural — mais baixa que seguir, pra um retorno suave, não um
// "salto" de volta pra formação.
const FACILIDADE_RETORNO = 0.045;
const DPR_MAXIMO = 2;

// Ciclo de vida de cada slot de constelação (ver ISlotConstelacao/
// atualizarSlot) — visível por 45–80s, escondido por 18–38s. A troca de
// constelação acontece durante o tempo escondido, só depois que o fade já
// chegou perto de 0 (ver LIMIAR_TROCA), nunca com ela ainda visível.
const DURACAO_VISIVEL_MIN = 60 * 45;
const DURACAO_VISIVEL_VAR = 60 * 35;
const DURACAO_ESCONDIDA_MIN = 60 * 18;
const DURACAO_ESCONDIDA_VAR = 60 * 20;
const FACILIDADE_FADE_CONSTELACAO = 0.03;
const LIMIAR_TROCA = 0.02;

const proximaDuracaoVisivel = (): number => Math.round(DURACAO_VISIVEL_MIN + Math.random() * DURACAO_VISIVEL_VAR);
const proximaDuracaoEscondida = (): number => Math.round(DURACAO_ESCONDIDA_MIN + Math.random() * DURACAO_ESCONDIDA_VAR);

// Quantidade de partículas SOLTAS (fora das constelações fixas) por área do
// painel — o painel escuro do login é bem mais estreito que a tela inteira
// (38%/42% dela, ver Login.tsx), por isso a régua aqui é modesta: nessa área
// menor, esse total já preenche bem ao redor das constelações sem virar uma
// teia densa (bastante espaço vazio entre os grupos).
const calcularQuantidadeSoltas = (largura: number, altura: number): number => {
  const area = largura * altura;
  const base = Math.round(area / 11000);
  return Math.max(14, Math.min(40, base));
};

const criarParticulaSolta = (largura: number, altura: number): IParticula => {
  // 3 níveis de profundidade, sorteados com peso (a maioria pequena/apagada
  // ao fundo, pouquíssimas mais brilhantes) — dá sensação de profundidade
  // sem ficar óbvio que são "camadas".
  const sorteio = Math.random();
  const nivel = sorteio < 0.55 ? 0 : sorteio < 0.9 ? 1 : 2;

  const velocidadeMax = nivel === 0 ? 0.025 : nivel === 1 ? 0.04 : 0.06;
  const raio = nivel === 0 ? 0.8 + Math.random() * 0.5 : nivel === 1 ? 1.1 + Math.random() * 0.5 : 1.4 + Math.random() * 0.6;
  const opacidadeBase = nivel === 0 ? 0.18 + Math.random() * 0.12 : nivel === 1 ? 0.32 + Math.random() * 0.15 : 0.5 + Math.random() * 0.25;

  const x = Math.random() * largura;
  const y = Math.random() * altura;
  return {
    x, y, naturalX: x, naturalY: y,
    vx: (Math.random() * 2 - 1) * velocidadeMax,
    vy: (Math.random() * 2 - 1) * velocidadeMax,
    raio,
    opacidadeBase,
  };
};

const criarGalaxiaInicial = (): IGalaxia => ({
  x: 0,
  y: 0,
  raio: 0,
  rotacao: Math.random() * Math.PI * 2,
  velocidadeRotacao: 0.00018 + Math.random() * 0.00018,
  opacidade: 0,
  ativa: false,
  // Primeira aparição some cedo (8–28s) — não fica escondida a visita
  // inteira, mas também não aparece "de cara" toda vez.
  framesRestantes: Math.round(60 * (8 + Math.random() * 20)),
});

// Escondida <-> ativa, alternando devagar (ativa por 20–35s, escondida por
// 30–70s) — cada vez que ativa de novo, reaparece num lugar/tamanho
// diferente. A opacidade nunca pula: sempre suavizada em direção ao alvo da
// fase atual (ver chamada em passo()), por isso entra/sai em fade.
function atualizarGalaxia(g: IGalaxia, largura: number, altura: number): void {
  g.rotacao += g.velocidadeRotacao;
  g.framesRestantes -= 1;
  if (g.framesRestantes <= 0) {
    g.ativa = !g.ativa;
    if (g.ativa) {
      g.x = largura * (0.12 + Math.random() * 0.76);
      g.y = altura * (0.12 + Math.random() * 0.76);
      g.raio = Math.min(largura, altura) * (0.16 + Math.random() * 0.09);
      g.framesRestantes = Math.round(60 * (20 + Math.random() * 15));
    } else {
      g.framesRestantes = Math.round(60 * (30 + Math.random() * 40));
    }
  }
  const alvo = g.ativa ? 1 : 0;
  // Facilidade bem baixa — fade lento (leva alguns segundos), condizente
  // com o resto da animação (nada aparece/some de repente).
  g.opacidade += (alvo - g.opacidade) * 0.02;
}

// Núcleo (brilho radial) + 2 "braços" achatados e rotacionados em torno dele
// — não é uma textura de espiral de verdade, só uma aproximação suave o
// bastante pra ler como galáxia sem competir com o resto da cena.
function desenharGalaxia(ctx: CanvasRenderingContext2D, g: IGalaxia): void {
  if (g.opacidade < 0.01 || g.raio <= 0) return;
  ctx.save();
  ctx.translate(g.x, g.y);
  ctx.rotate(g.rotacao);

  [0, Math.PI].forEach((anguloBraco) => {
    ctx.save();
    ctx.rotate(anguloBraco + 0.5);
    ctx.translate(g.raio * 0.3, 0);
    ctx.scale(1, 0.38);
    const halo = ctx.createRadialGradient(0, 0, 0, 0, 0, g.raio * 0.7);
    halo.addColorStop(0, `rgba(${COR_PONTO}, ${0.07 * g.opacidade})`);
    halo.addColorStop(1, `rgba(${COR_PONTO}, 0)`);
    ctx.fillStyle = halo;
    ctx.beginPath();
    ctx.arc(0, 0, g.raio * 0.7, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  });

  const nucleo = ctx.createRadialGradient(0, 0, 0, 0, 0, g.raio * 0.3);
  nucleo.addColorStop(0, `rgba(232, 250, 255, ${0.16 * g.opacidade})`);
  nucleo.addColorStop(1, `rgba(${COR_PONTO}, 0)`);
  ctx.fillStyle = nucleo;
  ctx.beginPath();
  ctx.arc(0, 0, g.raio * 0.3, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

// Cometa — cabeça brilhante + cauda em camadas (poeira larga e difusa, núcleo
// fino e brilhante, cauda de íons azulada levemente desviada, e faíscas
// soltas ficando pra trás), cruzando o painel na diagonal de vez em quando
// (ver criarCometa). Vive só enquanto atravessa: some em fade no fim da vida
// (vidaMax) ou ao sair da tela, o que vier primeiro. Clicar nele mostra o
// nome (ver handleClick), no mesmo rótulo de clique das estrelas.
interface IFaisca {
  x: number;
  y: number;
  vx: number;
  vy: number;
  raio: number;
  vida: number;
  vidaMax: number;
}

interface ICometa {
  x: number;
  y: number;
  vx: number;
  vy: number;
  comprimentoCauda: number;
  larguraCauda: number;
  vida: number; // frames desde que nasceu
  vidaMax: number;
  nome: string;
  faiscas: IFaisca[];
}

// Cometas reais — nome + um detalhe curto mostrados ao clicar (período
// orbital aproximado, ou o ano em que foi visto quando não é periódico).
const NOMES_COMETAS = [
  'Halley · volta a cada ~76 anos',
  'Hale-Bopp · volta a cada ~2.500 anos',
  'Encke · volta a cada 3,3 anos',
  'Swift-Tuttle · volta a cada ~133 anos',
  'Tempel-Tuttle · volta a cada ~33 anos',
  'Hyakutake · volta a cada ~70.000 anos',
  'NEOWISE · volta a cada ~6.800 anos',
  '67P/Churyumov-Gerasimenko · volta a cada 6,4 anos',
  'McNaught · visto em 2007',
  'Ikeya-Seki · visto em 1965',
  'Tsuchinshan-ATLAS · visto em 2024',
];

// Intervalo entre um cometa e o próximo — primeiro aparece em 6–12s,
// depois a cada 20–35s. Raro de propósito: uma surpresa discreta de vez em
// quando, sempre um de cada vez, sem competir com o formulário.
const primeiroIntervaloCometa = (): number => Math.round(60 * (6 + Math.random() * 6));
const proximoIntervaloCometa = (): number => Math.round(60 * (20 + Math.random() * 15));
const FRAMES_FADE_COMETA = 45;
const MAX_FAISCAS = 40;
// Multiplicador geral do brilho do cometa (todas as camadas) — baixo de
// propósito, pra ele passar discreto no fundo sem roubar a atenção do login.
const INTENSIDADE_COMETA = 0.5;

// Nasce na borda de cima (ou na lateral de onde vem) e desce na diagonal,
// entre ~20° e ~45° abaixo da horizontal, indo pra esquerda ou pra direita.
// Lento (0,7–1,2 px/frame) pra dar tempo de ver a cauda — e de clicar nele.
function criarCometa(largura: number, altura: number, nomeAnterior?: string): ICometa {
  const direcao = Math.random() < 0.5 ? 1 : -1;
  const angulo = 0.35 + Math.random() * 0.45;
  const velocidade = 0.7 + Math.random() * 0.5;
  const doTopo = Math.random() < 0.6;
  const candidatos = NOMES_COMETAS.filter((n) => n !== nomeAnterior);
  return {
    x: doTopo ? Math.random() * largura : direcao > 0 ? -20 : largura + 20,
    y: doTopo ? -20 : Math.random() * altura * 0.5,
    vx: Math.cos(angulo) * velocidade * direcao,
    vy: Math.sin(angulo) * velocidade,
    comprimentoCauda: 110 + Math.random() * 60,
    larguraCauda: 3.5 + Math.random() * 2,
    vida: 0,
    vidaMax: Math.round(60 * (11 + Math.random() * 5)),
    nome: candidatos[Math.floor(Math.random() * candidatos.length)],
    faiscas: [],
  };
}

// Opacidade geral do cometa — fade de entrada nos primeiros frames e de
// saída nos últimos, nunca aparece/some de repente.
const opacidadeCometa = (c: ICometa): number => {
  const entrada = Math.min(1, c.vida / FRAMES_FADE_COMETA);
  const saida = Math.min(1, (c.vidaMax - c.vida) / FRAMES_FADE_COMETA);
  return Math.max(0, Math.min(entrada, saida));
};

// false quando o cometa já terminou (fim da vida ou cauda inteira fora da
// tela) — quem chama remove ele da lista.
function atualizarCometa(c: ICometa, largura: number, altura: number): boolean {
  c.x += c.vx;
  c.y += c.vy;
  c.vida += 1;

  // Faíscas: nascem na cabeça a cada 2 frames, com um empurrão leve pra
  // trás/pros lados, e vão se apagando — é o "pó" brilhante da cauda.
  if (c.vida % 2 === 0 && c.faiscas.length < MAX_FAISCAS) {
    c.faiscas.push({
      x: c.x,
      y: c.y,
      vx: -c.vx * (0.15 + Math.random() * 0.25) + (Math.random() * 2 - 1) * 0.25,
      vy: -c.vy * (0.15 + Math.random() * 0.25) + (Math.random() * 2 - 1) * 0.25,
      raio: 0.4 + Math.random() * 0.7,
      vida: 0,
      vidaMax: Math.round(40 + Math.random() * 50),
    });
  }
  c.faiscas = c.faiscas.filter((f) => {
    f.x += f.vx;
    f.y += f.vy;
    f.vida += 1;
    return f.vida < f.vidaMax;
  });

  const m = c.comprimentoCauda;
  const foraDaTela = c.x < -m || c.x > largura + m || c.y > altura + m;
  return c.vida < c.vidaMax && !foraDaTela;
}

// Distância do ponto até a cabeça OU até a cauda (segmento cabeça→ponta) —
// usado pro clique: dá pra clicar em qualquer parte do cometa, não só
// acertar a cabeça em movimento.
function distanciaAteCometa(c: ICometa, x: number, y: number): number {
  const velocidade = Math.hypot(c.vx, c.vy);
  const ux = c.vx / velocidade;
  const uy = c.vy / velocidade;
  // Projeção do ponto no eixo da cauda (0 = cabeça, comprimentoCauda = ponta)
  const t = Math.max(0, Math.min(c.comprimentoCauda, -((x - c.x) * ux + (y - c.y) * uy)));
  return Math.hypot(x - (c.x - ux * t), y - (c.y - uy * t));
}

// Cauda afunilada (triângulo cabeça→ponta com a base arredondada na
// cabeça), preenchida com gradiente ao longo do comprimento — usada nas
// camadas de poeira e de núcleo, com larguras/cores/opacidades diferentes.
function desenharCaudaAfunilada(
  ctx: CanvasRenderingContext2D,
  c: ICometa,
  ux: number,
  uy: number,
  comprimento: number,
  largura: number,
  corCabeca: string,
  corMeio: string,
): void {
  const px = -uy;
  const py = ux;
  const pontaX = c.x - ux * comprimento;
  const pontaY = c.y - uy * comprimento;
  const gradiente = ctx.createLinearGradient(c.x, c.y, pontaX, pontaY);
  gradiente.addColorStop(0, corCabeca);
  gradiente.addColorStop(0.3, corMeio);
  gradiente.addColorStop(1, `rgba(${COR_PONTO}, 0)`);
  ctx.fillStyle = gradiente;
  ctx.beginPath();
  ctx.moveTo(c.x + px * largura, c.y + py * largura);
  ctx.lineTo(pontaX, pontaY);
  ctx.lineTo(c.x - px * largura, c.y - py * largura);
  ctx.arc(c.x, c.y, largura, Math.atan2(-py, -px), Math.atan2(py, px));
  ctx.closePath();
  ctx.fill();
}

function desenharCometa(ctx: CanvasRenderingContext2D, c: ICometa): void {
  const opacidade = opacidadeCometa(c) * INTENSIDADE_COMETA;
  if (opacidade < 0.01) return;

  const velocidade = Math.hypot(c.vx, c.vy);
  const ux = c.vx / velocidade;
  const uy = c.vy / velocidade;

  ctx.save();
  // 'lighter' soma as camadas de luz entre si — as sobreposições (cabeça,
  // início da cauda) ficam mais intensas, como um brilho de verdade.
  ctx.globalCompositeOperation = 'lighter';

  // 1) Cauda de íons — fina, azulada, mais longa e levemente desviada da
  // cauda principal (como nos cometas reais, que têm duas caudas).
  const desvio = 0.09 * Math.sign(c.vx || 1);
  const ionUx = ux * Math.cos(desvio) - uy * Math.sin(desvio);
  const ionUy = ux * Math.sin(desvio) + uy * Math.cos(desvio);
  const ionComprimento = c.comprimentoCauda * 1.25;
  const ion = ctx.createLinearGradient(c.x, c.y, c.x - ionUx * ionComprimento, c.y - ionUy * ionComprimento);
  ion.addColorStop(0, `rgba(140, 190, 255, ${0.35 * opacidade})`);
  ion.addColorStop(1, 'rgba(140, 190, 255, 0)');
  ctx.strokeStyle = ion;
  ctx.lineWidth = 1.2;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(c.x, c.y);
  ctx.lineTo(c.x - ionUx * ionComprimento, c.y - ionUy * ionComprimento);
  ctx.stroke();

  // 2) Cauda de poeira — larga e difusa, o "véu" principal.
  desenharCaudaAfunilada(
    ctx, c, ux, uy, c.comprimentoCauda, c.larguraCauda,
    `rgba(${COR_PONTO}, ${0.3 * opacidade})`,
    `rgba(${COR_PONTO}, ${0.14 * opacidade})`,
  );

  // 3) Núcleo da cauda — estreito e bem claro, dá definição por cima do véu.
  desenharCaudaAfunilada(
    ctx, c, ux, uy, c.comprimentoCauda * 0.7, c.larguraCauda * 0.3,
    `rgba(240, 253, 255, ${0.85 * opacidade})`,
    `rgba(${COR_PONTO}, ${0.35 * opacidade})`,
  );

  // 4) Faíscas soltas ficando pra trás.
  for (const f of c.faiscas) {
    const alfa = (1 - f.vida / f.vidaMax) * 0.7 * opacidade;
    ctx.fillStyle = `rgba(210, 248, 255, ${alfa})`;
    ctx.beginPath();
    ctx.arc(f.x, f.y, f.raio, 0, Math.PI * 2);
    ctx.fill();
  }

  // 5) Cabeça (coma) — halo largo + ponto branco no centro.
  const raioHalo = c.larguraCauda * 2.4;
  const halo = ctx.createRadialGradient(c.x, c.y, 0, c.x, c.y, raioHalo);
  halo.addColorStop(0, `rgba(255, 255, 255, ${0.95 * opacidade})`);
  halo.addColorStop(0.2, `rgba(200, 248, 255, ${0.55 * opacidade})`);
  halo.addColorStop(1, `rgba(${COR_PONTO}, 0)`);
  ctx.fillStyle = halo;
  ctx.beginPath();
  ctx.arc(c.x, c.y, raioHalo, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = `rgba(255, 255, 255, ${opacidade})`;
  ctx.beginPath();
  ctx.arc(c.x, c.y, 1.3, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

// Estado de um dos 3 slots visíveis — cada slot vive seu próprio ciclo de
// aparecer/sumir/trocar de constelação, independente dos outros dois (ver
// atualizarSlot). `particulas` é a lista de estrelas da constelação atual
// desse slot; é substituída por inteiro quando ele troca de constelação.
interface ISlotConstelacao {
  template: IConstelacaoTemplate;
  ancora: IAncoraGrupo;
  particulas: IParticula[];
  opacidade: number; // 0 a 1, sempre suavizada em direção ao alvo de `ativa`
  ativa: boolean;
  framesRestantes: number;
  // true entre o slot começar a sumir e a troca de constelação acontecer —
  // vira false assim que a troca é feita (ver atualizarSlot).
  trocaPendente: boolean;
}

// Posição/velocidade da âncora do slot + raio de wraparound, pro template
// dado — reaproveitado tanto na criação inicial quanto numa troca de
// constelação em runtime (o slot pode mudar de forma, o raio de wraparound
// muda junto).
function criarAncoraParaTemplate(largura: number, altura: number, template: IConstelacaoTemplate, indiceSlot: number, totalSlots: number): IAncoraGrupo {
  const escala = Math.max(0.55, Math.min(1.2, largura / 460));
  const raioGrupo = Math.max(...template.estrelas.map((e) => Math.hypot(e.x, e.y))) * escala + 20;
  const faixaAltura = altura / totalSlots;
  const centroFaixaY = faixaAltura * indiceSlot + faixaAltura / 2;
  return {
    x: largura * (0.28 + Math.random() * 0.44),
    y: centroFaixaY + (Math.random() * 2 - 1) * (faixaAltura * 0.2),
    vx: (Math.random() * 2 - 1) * 0.01,
    vy: (Math.random() * 2 - 1) * 0.008,
    raioGrupo,
  };
}

// Lista de estrelas (partículas) da constelação atual de um slot, ancoradas
// nele — reconstruída inteira sempre que o slot troca de constelação ou o
// painel é redimensionado.
function criarParticulasSlot(largura: number, indiceSlot: number, ancora: IAncoraGrupo, template: IConstelacaoTemplate): IParticula[] {
  const escala = Math.max(0.55, Math.min(1.2, largura / 460));
  return template.estrelas.map((estrela) => {
    const offsetX = estrela.x * escala;
    const offsetY = estrela.y * escala;
    const naturalX = ancora.x + offsetX;
    const naturalY = ancora.y + offsetY;
    return {
      x: naturalX,
      y: naturalY,
      naturalX,
      naturalY,
      vx: 0,
      vy: 0,
      raio: 1.3 + estrela.brilho * 1.1,
      opacidadeBase: 0.35 + estrela.brilho * 0.5,
      grupo: {
        slotIndex: indiceSlot,
        offsetX, offsetY,
        nomeConstelacao: template.nome,
        nomeEstrela: estrela.nome,
        distanciaAnosLuz: estrela.distanciaAnosLuz,
      },
    };
  });
}

// Avança o ciclo aparecer/sumir/trocar de um slot — chamado 1x por frame por
// slot. Quando o tempo visível acaba, começa a sumir (ativa=false); quando o
// fade já está bem baixo (LIMIAR_TROCA), troca de constelação nesse instante
// (nunca antes, pra não "piscar" pra uma forma nova ainda visível); quando o
// tempo escondido acaba, volta a aparecer (já com a constelação nova).
function atualizarSlot(slot: ISlotConstelacao, indiceSlot: number, todosSlots: ISlotConstelacao[], largura: number, altura: number): void {
  slot.framesRestantes -= 1;
  if (slot.framesRestantes <= 0) {
    if (slot.ativa) {
      slot.ativa = false;
      slot.trocaPendente = true;
      slot.framesRestantes = proximaDuracaoEscondida();
    } else {
      slot.ativa = true;
      slot.framesRestantes = proximaDuracaoVisivel();
    }
  }

  if (slot.trocaPendente && slot.opacidade < LIMIAR_TROCA) {
    const templatesEmUso = todosSlots.map((s) => s.template);
    slot.template = escolherProximaConstelacao(templatesEmUso);
    slot.ancora = criarAncoraParaTemplate(largura, altura, slot.template, indiceSlot, todosSlots.length);
    slot.particulas = criarParticulasSlot(largura, indiceSlot, slot.ancora, slot.template);
    slot.trocaPendente = false;
  }

  const alvo = slot.ativa ? 1 : 0;
  slot.opacidade += (alvo - slot.opacidade) * FACILIDADE_FADE_CONSTELACAO;
}

export const ConstellationBackground: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduzMovimento = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Trio inicial, escolhido uma vez por visita conforme a hora de quando a
    // tela de login abriu (ver escolherConstelacoesDoHorario) — a partir daí
    // cada slot passa a girar sozinho pelo pool inteiro (ver atualizarSlot).
    const constelacoesIniciais = escolherConstelacoesDoHorario(new Date().getHours());
    const galaxia = criarGalaxiaInicial();
    // Cometas em trânsito agora (normalmente 0 ou 1) + contagem regressiva
    // até o próximo nascer — só avançam dentro de passo(), então com
    // prefers-reduced-motion nenhum cometa chega a aparecer.
    let cometas: ICometa[] = [];
    let framesAteProximoCometa = primeiroIntervaloCometa();
    // Evita o mesmo nome duas vezes seguidas.
    let ultimoNomeCometa: string | undefined;

    let largura = 0;
    let altura = 0;
    let slots: ISlotConstelacao[] = [];
    let soltas: IParticula[] = [];
    // Snapshot da última lista combinada (slots + soltas) montada em passo() —
    // reaproveitada pelo handleClick, que roda fora do loop de animação.
    let ultimasParticulas: IParticula[] = [];
    let animId = 0;
    // Posição do mouse relativa ao painel — null quando o cursor está fora
    // (a interação precisa "desligar" nesse caso, não continuar com a
    // última posição conhecida).
    const mouse = { x: null as number | null, y: null as number | null };
    // Cache do rect do container — recalculado só no resize (não a cada
    // movimento do mouse). O canvas é pointer-events:none de propósito (não
    // pode bloquear clique/seleção de texto do painel), então o listener de
    // mouse fica no window e filtra manualmente se o cursor está dentro da
    // área do painel, em vez de depender do container receber o evento.
    let rectAtual = { left: 0, top: 0 };

    // Rótulo discreto (nome da constelação + da estrela, quando tiver) que
    // aparece perto do mouse quando ele está sobre a partícula selecionada —
    // opacidade some/aparece com um fade suave (ver passo()), nunca some/
    // aparece de repente. A posição fica "congelada" na última partícula
    // válida enquanto o fade de saída ainda não terminou, pra não pular de
    // lugar quando o mouse sai de perto de uma estrela nomeada.
    let opacidadeLabel = 0;
    let textoLabel = '';
    let posicaoLabel = { x: 0, y: 0, raio: 0 };

    // Rótulo de CLIQUE (distância em anos-luz) — independente do rótulo de
    // hover acima: fica visível por um tempo fixo depois do clique
    // (cliqueFramesRestantes, contado em passo()) e só então começa a
    // desaparecer, também em fade. Desenhado abaixo da estrela (o de hover
    // fica acima), pra não sobrepor quando os dois aparecem juntos.
    let opacidadeClique = 0;
    let textoClique = '';
    let posicaoClique = { x: 0, y: 0, raio: 0 };
    let cliqueFramesRestantes = 0;
    // Qual partícula foi clicada por último — a posição do rótulo de clique
    // acompanha ela frame a frame (ver passo()), independente de qual está
    // selecionada/sendo seguida pelo mouse no momento. Guardamos a
    // referência (não um índice) porque o slot dela pode trocar de
    // constelação e recriar sua lista de partículas enquanto o rótulo ainda
    // está desaparecendo.
    let particulaClicada: IParticula | null = null;
    // Mesma ideia, pra quando o clique foi num cometa — o rótulo de clique
    // acompanha a cabeça dele enquanto atravessa o painel. Só um dos dois
    // (particulaClicada/cometaClicado) fica preenchido por vez.
    let cometaClicado: ICometa | null = null;

    const obterTodasParticulas = (): IParticula[] => {
      const resultado: IParticula[] = [];
      for (const slot of slots) resultado.push(...slot.particulas);
      resultado.push(...soltas);
      return resultado;
    };

    const redimensionar = () => {
      const rect = container.getBoundingClientRect();
      rectAtual = { left: rect.left, top: rect.top };
      largura = rect.width;
      altura = rect.height;
      const dpr = Math.min(window.devicePixelRatio || 1, DPR_MAXIMO);
      canvas.width = Math.max(1, Math.floor(largura * dpr));
      canvas.height = Math.max(1, Math.floor(altura * dpr));
      canvas.style.width = `${largura}px`;
      canvas.style.height = `${altura}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const totalSlots = constelacoesIniciais.length;
      if (slots.length === 0) {
        slots = constelacoesIniciais.map((template, indice) => {
          const ancora = criarAncoraParaTemplate(largura, altura, template, indice, totalSlots);
          return {
            template,
            ancora,
            particulas: criarParticulasSlot(largura, indice, ancora, template),
            opacidade: 1,
            ativa: true,
            framesRestantes: proximaDuracaoVisivel(),
            trocaPendente: false,
          };
        });
      } else {
        // Resize: mantém a constelação e a fase (visível/escondida/opacidade)
        // de cada slot, só reposiciona a âncora e reconstrói as estrelas pra
        // nova escala/tamanho do painel.
        slots.forEach((slot, indice) => {
          slot.ancora = criarAncoraParaTemplate(largura, altura, slot.template, indice, totalSlots);
          slot.particulas = criarParticulasSlot(largura, indice, slot.ancora, slot.template);
        });
      }

      const quantidadeSoltas = calcularQuantidadeSoltas(largura, altura);
      soltas = Array.from({ length: quantidadeSoltas }, () => criarParticulaSolta(largura, altura));
    };

    const desenharFrameEstatico = () => {
      // prefers-reduced-motion: sem loop de animação — só um instantâneo
      // parado (pontos + linhas de quem já nasceu próximo o bastante; a
      // galáxia nunca chega a ativar aqui, opacidade fica em 0 — sem
      // atualizarGalaxia() sendo chamado, não há o que desenhar). Os slots
      // também nunca giram (atualizarSlot só roda dentro de passo()).
      const todas = obterTodasParticulas();
      ctx.clearRect(0, 0, largura, altura);
      desenharGalaxia(ctx, galaxia);
      desenharConexoesDinamicas(todas);
      desenharConstelacoesFixas();
      desenharParticulas(todas);
    };

    // Linhas finíssimas e bem apagadas entre QUALQUER par de partículas
    // (soltas ou de constelação) que estejam próximas no momento — é o que
    // dá o efeito de "constelação dinâmica" ao redor das fixas. Partículas de
    // um slot em fade têm a opacidade da linha reduzida junto (produto dos
    // dois multiplicadores), pra elas somem/apareçam por igual às estrelas.
    const desenharConexoesDinamicas = (todas: IParticula[]) => {
      for (let i = 0; i < todas.length; i++) {
        for (let j = i + 1; j < todas.length; j++) {
          const a = todas[i];
          const b = todas[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const distancia = Math.sqrt(dx * dx + dy * dy);
          if (distancia >= DISTANCIA_MAX_LINHA) continue;

          const multiplicadorA = a.grupo ? slots[a.grupo.slotIndex].opacidade : 1;
          const multiplicadorB = b.grupo ? slots[b.grupo.slotIndex].opacidade : 1;
          const proximidade = 1 - distancia / DISTANCIA_MAX_LINHA;
          // Base bem baixa (0.04–0.15) — nunca chega perto de competir com
          // o texto por cima.
          const opacidadeLinha = (0.04 + proximidade * 0.11) * multiplicadorA * multiplicadorB;
          if (opacidadeLinha < 0.003) continue;
          ctx.strokeStyle = `rgba(${COR_PONTO}, ${opacidadeLinha})`;
          ctx.lineWidth = 0.4 + proximidade * 0.4;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }
    };

    // Arestas FIXAS do desenho clássico da constelação atual de cada slot —
    // sempre desenhadas (não dependem de distância), com opacidade um pouco
    // maior que as conexões dinâmicas pra formar o "contorno" reconhecível
    // por cima do resto, e escaladas pela opacidade de fade do próprio slot.
    const desenharConstelacoesFixas = () => {
      slots.forEach((slot) => {
        if (slot.opacidade < 0.01) return;
        ctx.strokeStyle = `rgba(${COR_PONTO}, ${0.22 * slot.opacidade})`;
        ctx.lineWidth = 0.7;
        slot.template.arestas.forEach(([de, para]) => {
          const a = slot.particulas[de];
          const b = slot.particulas[para];
          if (!a || !b) return;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        });
      });
    };

    const desenharParticulas = (todas: IParticula[]) => {
      for (const p of todas) {
        const multiplicador = p.grupo ? slots[p.grupo.slotIndex].opacidade : 1;
        if (multiplicador < 0.01) continue;
        ctx.beginPath();
        ctx.fillStyle = `rgba(${COR_PONTO}, ${p.opacidadeBase * multiplicador})`;
        ctx.arc(p.x, p.y, p.raio, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    // Nome da constelação (+ da estrela, se tiver) acima do ponto selecionado
    // — some/aparece com fade (opacidadeLabel), nunca abrupto. Fonte pequena,
    // opacidade final baixa, brilho suave em vez de caixa de fundo — pra
    // ficar discreto e não competir com o texto do formulário ao lado.
    const desenharLabel = () => {
      if (opacidadeLabel < 0.01 || !textoLabel) return;
      ctx.save();
      ctx.font = '600 11px "Segoe UI", Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.shadowColor = `rgba(${COR_PONTO}, ${opacidadeLabel * 0.5})`;
      ctx.shadowBlur = 6;
      ctx.fillStyle = `rgba(226, 250, 255, ${opacidadeLabel * 0.8})`;
      ctx.fillText(textoLabel, posicaoLabel.x, posicaoLabel.y - posicaoLabel.raio - 8);
      ctx.restore();
    };

    // Distância em anos-luz — só aparece depois de um CLIQUE numa estrela
    // nomeada (ver handleClick). Fica ACIMA dela, um pouco mais alto que o
    // rótulo de hover (ver desenharLabel) pra não sobrepor quando os dois
    // aparecem juntos — nunca embaixo, porque ali o próprio cursor do mouse
    // (que acabou de clicar bem naquele ponto) tampava o texto.
    const desenharClique = () => {
      if (opacidadeClique < 0.01 || !textoClique) return;
      ctx.save();
      ctx.font = '500 10.5px "Segoe UI", Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.shadowColor = `rgba(${COR_PONTO}, ${opacidadeClique * 0.5})`;
      ctx.shadowBlur = 6;
      ctx.fillStyle = `rgba(226, 250, 255, ${opacidadeClique * 0.75})`;
      // Mantém o texto inteiro dentro do painel — os nomes de cometa são
      // longos e o cometa pode estar colado na borda quando é clicado.
      const metade = ctx.measureText(textoClique).width / 2 + 8;
      const xTexto = Math.max(metade, Math.min(largura - metade, posicaoClique.x));
      const yTexto = Math.max(16, posicaoClique.y - posicaoClique.raio - 22);
      ctx.fillText(textoClique, xTexto, yTexto);
      ctx.restore();
    };

    const passo = () => {
      atualizarGalaxia(galaxia, largura, altura);
      slots.forEach((slot, indice) => atualizarSlot(slot, indice, slots, largura, altura));

      framesAteProximoCometa -= 1;
      if (framesAteProximoCometa <= 0) {
        const novo = criarCometa(largura, altura, ultimoNomeCometa);
        ultimoNomeCometa = novo.nome;
        cometas.push(novo);
        framesAteProximoCometa = proximoIntervaloCometa();
      }
      cometas = cometas.filter((c) => atualizarCometa(c, largura, altura));
      // Cometa clicado já saiu/apagou — o rótulo começa a sumir junto, em
      // vez de ficar parado no último ponto por onde ele passou.
      if (cometaClicado && !cometas.includes(cometaClicado)) {
        cometaClicado = null;
        cliqueFramesRestantes = 0;
      }

      // 1) Avança as âncoras dos slots — sempre, bem devagar.
      for (const slot of slots) {
        const ancora = slot.ancora;
        ancora.x += ancora.vx;
        ancora.y += ancora.vy;
        const m = ancora.raioGrupo;
        if (ancora.x < -m) ancora.x = largura + m;
        else if (ancora.x > largura + m) ancora.x = -m;
        if (ancora.y < -m) ancora.y = altura + m;
        else if (ancora.y > altura + m) ancora.y = -m;
      }

      // 2) Avança a posição NATURAL de cada partícula — soltas seguem a
      // própria vx/vy (com wraparound); estrelas de constelação seguem a
      // âncora do próprio slot (mantendo sempre a forma fixa).
      for (const slot of slots) {
        for (const p of slot.particulas) {
          p.naturalX = slot.ancora.x + p.grupo!.offsetX;
          p.naturalY = slot.ancora.y + p.grupo!.offsetY;
        }
      }
      for (const p of soltas) {
        p.naturalX += p.vx;
        p.naturalY += p.vy;
        if (p.naturalX < -10) p.naturalX = largura + 10;
        else if (p.naturalX > largura + 10) p.naturalX = -10;
        if (p.naturalY < -10) p.naturalY = altura + 10;
        else if (p.naturalY > altura + 10) p.naturalY = -10;
      }

      const todas = obterTodasParticulas();
      ultimasParticulas = todas;

      // 3) Escolhe QUAL partícula (no máximo uma) está sendo "puxada" pro
      // mouse agora — sempre a mais próxima dele (pela posição natural, não
      // a exibida, pra não realimentar a própria seleção), dentro do raio de
      // captura. Sem mouse no painel, nenhuma é selecionada.
      let indiceSelecionado = -1;
      if (mouse.x !== null && mouse.y !== null) {
        let menorDistancia = DISTANCIA_MOUSE;
        for (let i = 0; i < todas.length; i++) {
          const p = todas[i];
          const dx = mouse.x - p.naturalX;
          const dy = mouse.y - p.naturalY;
          const distancia = Math.sqrt(dx * dx + dy * dy);
          if (distancia < menorDistancia) {
            menorDistancia = distancia;
            indiceSelecionado = i;
          }
        }
      }

      // 4) Só a selecionada anda em direção ao cursor; TODAS as outras
      // (incluindo quem acabou de ser solta) são reconduzidas suavemente até
      // a própria posição natural — é isso que faz a anterior "voltar pra
      // formação que ela estava".
      for (let i = 0; i < todas.length; i++) {
        const p = todas[i];
        if (i === indiceSelecionado && mouse.x !== null && mouse.y !== null) {
          p.x += (mouse.x - p.x) * FACILIDADE_SEGUIR;
          p.y += (mouse.y - p.y) * FACILIDADE_SEGUIR;
        } else {
          p.x += (p.naturalX - p.x) * FACILIDADE_RETORNO;
          p.y += (p.naturalY - p.y) * FACILIDADE_RETORNO;
        }
      }

      // 5) Rótulo: só quando a selecionada é uma estrela de constelação
      // (pontos soltos não têm nome pra mostrar). Fade suave de entrada e
      // saída — não é um show/hide abrupto.
      const selecionada = indiceSelecionado >= 0 ? todas[indiceSelecionado] : null;
      if (selecionada?.grupo) {
        const { nomeConstelacao, nomeEstrela } = selecionada.grupo;
        textoLabel = nomeEstrela ? `${nomeConstelacao} · ${nomeEstrela}` : nomeConstelacao;
        posicaoLabel = { x: selecionada.x, y: selecionada.y, raio: selecionada.raio };
        opacidadeLabel += (1 - opacidadeLabel) * 0.12;
      } else {
        opacidadeLabel += (0 - opacidadeLabel) * 0.12;
      }

      // 6) Rótulo de clique: acompanha a partícula clicada por último
      // enquanto ela existir, com contagem regressiva antes de começar a
      // desaparecer (ver handleClick, que reseta cliqueFramesRestantes).
      if (cometaClicado) {
        posicaoClique = { x: cometaClicado.x, y: cometaClicado.y, raio: cometaClicado.larguraCauda };
      } else if (particulaClicada) {
        posicaoClique = { x: particulaClicada.x, y: particulaClicada.y, raio: particulaClicada.raio };
      }
      if (cliqueFramesRestantes > 0) {
        cliqueFramesRestantes -= 1;
        opacidadeClique += (1 - opacidadeClique) * 0.15;
      } else {
        opacidadeClique += (0 - opacidadeClique) * 0.06;
      }

      ctx.clearRect(0, 0, largura, altura);
      desenharGalaxia(ctx, galaxia);
      desenharConexoesDinamicas(todas);
      desenharConstelacoesFixas();
      desenharParticulas(todas);
      cometas.forEach((c) => desenharCometa(ctx, c));
      desenharLabel();
      desenharClique();
      animId = requestAnimationFrame(passo);
    };

    redimensionar();
    if (reduzMovimento) {
      desenharFrameEstatico();
    } else {
      animId = requestAnimationFrame(passo);
    }

    // Listener no window (não no container): o canvas é pointer-events:none
    // de propósito (não pode bloquear clique/seleção de texto por cima), o
    // que também significa que ELE NUNCA RECEBE eventos de mouse — por isso
    // o listener fica no window e filtra manualmente se o cursor está
    // dentro da área do painel, usando o rect cacheado.
    const handleMouseMove = (event: MouseEvent) => {
      const x = event.clientX - rectAtual.left;
      const y = event.clientY - rectAtual.top;
      if (x < 0 || y < 0 || x > largura || y > altura) {
        mouse.x = null;
        mouse.y = null;
        return;
      }
      mouse.x = x;
      mouse.y = y;
    };

    // Clique numa estrela nomeada mostra a distância em anos-luz (rótulo
    // acima dela, ver desenharClique) — só nas que têm distanciaAnosLuz
    // (estrelas de verdade, não pontos soltos nem conectores sem nome).
    // Mesmo raciocínio de handleMouseMove: listener no window, filtrado
    // manualmente pela área do painel.
    const RAIO_CLIQUE = 26;
    const RAIO_CLIQUE_COMETA = 22;
    const handleClick = (event: MouseEvent) => {
      const x = event.clientX - rectAtual.left;
      const y = event.clientY - rectAtual.top;
      if (x < 0 || y < 0 || x > largura || y > altura) return;

      // Cometa tem prioridade sobre estrela — é o que está em movimento e
      // mais chama atenção; se o clique pegou nele (cabeça ou cauda),
      // mostra o nome e para por aqui.
      let cometaMaisProximo: ICometa | null = null;
      let menorDistanciaCometa = RAIO_CLIQUE_COMETA;
      for (const c of cometas) {
        if (opacidadeCometa(c) < 0.2) continue;
        const distancia = distanciaAteCometa(c, x, y);
        if (distancia < menorDistanciaCometa) {
          menorDistanciaCometa = distancia;
          cometaMaisProximo = c;
        }
      }
      if (cometaMaisProximo) {
        textoClique = `Cometa ${cometaMaisProximo.nome}`;
        cometaClicado = cometaMaisProximo;
        particulaClicada = null;
        cliqueFramesRestantes = 60 * 5;
        return;
      }

      let particulaMaisProxima: IParticula | null = null;
      let menorDistancia = RAIO_CLIQUE;
      for (const p of ultimasParticulas) {
        if (!p.grupo?.nomeEstrela || p.grupo.distanciaAnosLuz === undefined) continue;
        const distancia = Math.hypot(p.x - x, p.y - y);
        if (distancia < menorDistancia) {
          menorDistancia = distancia;
          particulaMaisProxima = p;
        }
      }
      if (!particulaMaisProxima) return;

      const estrela = particulaMaisProxima.grupo!;
      const distanciaFormatada = estrela.distanciaAnosLuz!.toLocaleString('pt-BR');
      textoClique = `${distanciaFormatada} anos-luz da Terra`;
      particulaClicada = particulaMaisProxima;
      cometaClicado = null;
      cliqueFramesRestantes = 60 * 5; // ~5s visível antes de começar o fade de saída
    };

    if (!reduzMovimento) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('click', handleClick);
    }

    const resizeObserver = new ResizeObserver(() => {
      redimensionar();
      if (reduzMovimento) desenharFrameEstatico();
    });
    resizeObserver.observe(container);

    return () => {
      if (animId) cancelAnimationFrame(animId);
      resizeObserver.disconnect();
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('click', handleClick);
    };
  }, []);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 1,
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
    >
      <canvas ref={canvasRef} style={{ display: 'block' }} />
    </Box>
  );
};
